'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useCallback, useId, useMemo, useState } from 'react'
import { useWorkspaceScopedLoader, type WorkspaceScopedLoadContext } from '@kiao-voice/hooks'
import { isTeamMemberDisabledError } from '@kiao-voice/api-client'
import type { WritableNotesTemplateScope } from '@kiao-voice/core'
import { useTranslation } from 'react-i18next'
import { useAuthModal } from '@/features/auth/auth-modal-provider'
import { readStoredToken } from '@/features/auth/token-storage'
import {
  Button,
  Field,
  Input,
  KiaoModal,
  Switch,
  Tag,
  Textarea,
  ViewTabPanel,
  ViewTabs,
  useKiaoConfirm,
  toast,
} from '@kiao-voice/ui-kit'
import { WorkspacePageHeader, useWorkspaceIndicator } from '@/components/workspace-indicator'
import {
  createNotesTemplate,
  deleteNotesTemplate,
  fetchNotesTemplates,
  updateNotesTemplate,
} from '@/features/meeting-notes/api-client'
import type { NotesTemplate } from '@/features/meeting-notes/types'
import { TemplatePreview } from '@/features/meeting-notes/template-preview'
import {
  canSaveTemplate,
  findTemplateProblems,
} from '@/features/meeting-notes/template-preview-model'
import './templates.css'

type Translate = ReturnType<typeof useTranslation<'web'>>['t']
type Scope = WritableNotesTemplateScope
type Tab = 'mine' | 'team' | 'builtin'

interface Props {
  /** Test seam for "is anyone signed in?" — the real one reads the stored token. */
  hasTokenFn?: () => boolean
  fetchTemplatesFn?: typeof fetchNotesTemplates
  createTemplateFn?: typeof createNotesTemplate
  updateTemplateFn?: typeof updateNotesTemplate
  deleteTemplateFn?: typeof deleteNotesTemplate
}

/** Draft held by the drawer. `id === null` means "not saved yet". */
interface Draft {
  id: number | null
  name: string
  description: string
  markdown: string
  scope: Scope
  isDefault: boolean
  /** Built-ins open read-only; the primary action becomes 複製成我的. */
  readOnly: boolean
}

const NEW_TEMPLATE_MARKDOWN = '## 摘要\n\n## 決議\n\n## 待辦\n'

/**
 * Module-level so the identity is STABLE across renders.
 *
 * Written inline as a default parameter (`hasTokenFn = () => …`) this allocates
 * a new function every render, which changes `load`'s `useCallback` identity,
 * which re-fires the `useEffect` that calls it, which sets state, which
 * re-renders — an unbounded request loop. It hammered the API at ~75 req/s
 * before it was caught.
 */
const hasStoredToken = () => readStoredToken() !== null

/**
 * 整理範本 (/app/templates).
 *
 * 我的 ／ 團隊 ／ 內建 are TABS, not stacked sections, and the tab is what says
 * where a template comes from; no row carries a source tag. Self-authored
 * templates grow to twenty or thirty; stacked sections would push 內建 below the
 * fold, and that is exactly where a new user goes first. Tabs keep every source
 * at the same place on screen no matter how long any list gets. The team tab
 * exists only where there can be team templates: a personal workspace shows two.
 *
 * Editing happens in a right-hand drawer rather than a route: a template is a
 * long free-form markdown body, and losing the list behind it costs the one
 * thing you want while writing one — seeing what you already have.
 */
export function TemplatesPage({
  hasTokenFn = hasStoredToken,
  fetchTemplatesFn = fetchNotesTemplates,
  createTemplateFn = createNotesTemplate,
  updateTemplateFn = updateNotesTemplate,
  deleteTemplateFn = deleteNotesTemplate,
}: Props) {
  const { t } = useTranslation('web')
  const confirm = useKiaoConfirm()
  const { open: openSignIn } = useAuthModal()
  const workspace = useWorkspaceIndicator()
  const teamName = workspace?.mode === 'team' ? workspace.name : null

  const [templates, setTemplates] = useState<NotesTemplate[]>([])
  const [writableScopes, setWritableScopes] = useState<Scope[]>(['personal'])
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error' | 'needsAuth'>('loading')
  const [tab, setTab] = useState<Tab>('mine')
  const [draft, setDraft] = useState<Draft | null>(null)
  const [saving, setSaving] = useState(false)

  const loadWorkspace = useCallback(
    async ({ isCurrent }: WorkspaceScopedLoadContext) => {
      // Answer "you are not signed in" BEFORE trying to fetch. Without a token the
      // client throws locally — the request never even reaches the server — so the
      // catch below saw an ordinary failure and told the visitor to「再試一次」,
      // which can never work and names no way forward. Every other /app page
      // checks the token first; this one did not. Same order as
      // `transcriptions-list-page`.
      if (!hasTokenFn()) {
        if (isCurrent()) setPhase('needsAuth')
        return
      }
      try {
        const res = await fetchTemplatesFn()
        if (!isCurrent()) return
        setTemplates(res.templates)
        setWritableScopes(res.writableScopes)
        setPhase('ready')
      } catch (error) {
        reportHandledException(error, { source: 'app/app/templates/templates-page.tsx' })
        if (!isCurrent()) return
        console.error('[templates] failed to load', error)
        // A 401 that arrives despite a stored token means the token just died —
        // still a sign-in problem, not a retryable one.
        const status = (error as { status?: number } | null)?.status
        setPhase(
          (status === 401 || status === 403) && !isTeamMemberDisabledError(error)
            ? 'needsAuth'
            : 'error'
        )
      }
    },
    [fetchTemplatesFn, hasTokenFn]
  )

  const clearWorkspace = useCallback(() => {
    setTemplates([])
    setWritableScopes(['personal'])
    setPhase('loading')
    setTab('mine')
    setDraft(null)
    setSaving(false)
  }, [])

  const { reload: load } = useWorkspaceScopedLoader({
    load: loadWorkspace,
    clear: clearWorkspace,
  })

  const mine = useMemo(
    () => templates.filter((template) => !template.isBuiltin && template.scope === 'personal'),
    [templates]
  )
  const team = useMemo(
    () => templates.filter((template) => template.scope === 'organization'),
    [templates]
  )
  const builtin = useMemo(() => templates.filter((template) => template.isBuiltin), [templates])
  const canWriteTeam = writableScopes.includes('organization')
  const showTeamTab = canWriteTeam || team.length > 0

  /** A new template belongs where the user is standing: the team tab makes a team one. */
  const openNew = () =>
    setDraft({
      id: null,
      name: '',
      description: '',
      markdown: NEW_TEMPLATE_MARKDOWN,
      scope: tab === 'team' && canWriteTeam ? 'organization' : 'personal',
      isDefault: false,
      readOnly: false,
    })

  const openExisting = (template: NotesTemplate) =>
    setDraft({
      id: template.id,
      name: template.name,
      description: template.description,
      markdown: template.markdown,
      scope: template.scope === 'organization' ? 'organization' : 'personal',
      isDefault: template.isDefault,
      readOnly: template.isBuiltin,
    })

  /**
   * Open an UNSAVED copy in the drawer. Nothing is written until the user saves.
   *
   * This used to POST the copy first and then open the drawer on the result, so
   * pressing 取消 left a template behind that the user had just declined — the
   * drawer looked like a confirmation step and wasn't one.
   */
  const openCopyDraft = (template: NotesTemplate) => {
    // NOT `setTab('mine')` here — switching tabs is a consequence of the copy
    // existing, and the copy does not exist until the drawer is saved. Doing it
    // on open moves the user off the tab they were browsing even when they only
    // glanced at the drawer and pressed 取消.
    setDraft({
      id: null,
      name: t('transcriptions.templates.copyName', { name: template.name }).slice(0, 120),
      description: template.description,
      markdown: template.markdown,
      // A copy never inherits `isDefault`: one default per scope is a DB
      // constraint, and silently demoting the current one would change which
      // template the next 整理 picks without the user asking.
      scope: writableScopes[0] ?? 'personal',
      isDefault: false,
      readOnly: false,
    })
  }

  const handleDelete = async (template: NotesTemplate) => {
    const ok = await confirm({
      title: t('transcriptions.templates.deleteTitle'),
      message:
        template.scope !== 'organization'
          ? t('transcriptions.templates.deleteConfirm')
          : teamName
            ? t('transcriptions.templates.deleteConfirmTeam', { workspace: teamName })
            : t('transcriptions.templates.deleteConfirmTeamNoName'),
      confirmText: t('transcriptions.templates.deleteOk'),
      cancelText: t('transcriptions.confirm.cancel'),
    })
    if (!ok) return
    try {
      await deleteTemplateFn(template.id)
      setDraft(null)
      await load()
      toast.success(t('transcriptions.templates.deletedToast', { name: template.name }))
    } catch (error) {
      reportHandledException(error, { source: 'app/app/templates/templates-page.tsx' })
      console.error('[templates] delete failed', error)
      toast.error(t('transcriptions.templates.saveFailed'))
    }
  }

  const handleSave = async () => {
    if (!draft) return
    if (draft.readOnly) {
      const source = templates.find((template) => template.id === draft.id)
      if (source) openCopyDraft(source)
      return
    }
    if (!draft.name.trim()) {
      toast.error(t('transcriptions.templates.needName'))
      return
    }
    setSaving(true)
    try {
      if (draft.id === null) {
        await createTemplateFn({
          name: draft.name.trim(),
          description: draft.description.trim(),
          markdown: draft.markdown,
          scope: draft.scope,
          isDefault: draft.isDefault,
        })
      } else {
        await updateTemplateFn(draft.id, {
          name: draft.name.trim(),
          description: draft.description.trim(),
          markdown: draft.markdown,
          isDefault: draft.isDefault,
        })
      }
      setDraft(null)
      await load()
      // Land on the tab the template now lives in, or the save looks like it did nothing.
      setTab(draft.scope === 'organization' ? 'team' : 'mine')
      toast.success(t('transcriptions.templates.savedToast'))
    } catch (error) {
      reportHandledException(error, { source: 'app/app/templates/templates-page.tsx' })
      console.error('[templates] save failed', error)
      toast.error(t('transcriptions.templates.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <WorkspacePageHeader
        title={t('transcriptions.templates.pageTitle')}
        subtitle={t('transcriptions.templates.pageLead')}
        actions={
          <Button variant="primary" size="sm" onClick={openNew}>
            {t('transcriptions.templates.new')}
          </Button>
        }
      />

      <ViewTabs
        className="tp-tabs"
        tabs={[
          { id: 'mine', label: t('transcriptions.templates.tabMine'), count: mine.length },
          ...(showTeamTab
            ? [{ id: 'team', label: t('transcriptions.templates.tabTeam'), count: team.length }]
            : []),
          { id: 'builtin', label: t('transcriptions.templates.tabBuiltin'), count: builtin.length },
        ]}
        activeId={tab}
        onChange={(id) => setTab(id as Tab)}
        label={t('transcriptions.templates.tabsLabel')}
      />

      {phase === 'loading' ? <p className="tp-empty">{t('transcriptions.loading')}</p> : null}
      {phase === 'needsAuth' ? (
        <div className="tp-empty">
          <p>{t('transcriptions.templates.needAuthHeading')}</p>
          <p className="lead">{t('transcriptions.templates.needAuthBody')}</p>
          <Button size="sm" onClick={() => openSignIn({ redirectTo: null })}>
            {t('transcriptions.signInCta')}
          </Button>
        </div>
      ) : null}
      {phase === 'error' ? (
        <p className="tp-empty">{t('transcriptions.templates.loadFailed')}</p>
      ) : null}

      {phase === 'ready' ? (
        <>
          <ViewTabPanel id="mine" active={tab === 'mine'} className="tp-panel">
            {mine.length > 0 ? (
              mine.map((template) => (
                <TemplateRow
                  key={template.id}
                  template={template}
                  t={t}
                  onEdit={() => openExisting(template)}
                  onDuplicate={() => openCopyDraft(template)}
                  onDelete={() => void handleDelete(template)}
                />
              ))
            ) : (
              <p className="tp-empty">
                {builtin.length > 0 ? (
                  <>
                    {t('transcriptions.templates.emptyMinePrefix')}
                    <button className="tp-jump" type="button" onClick={() => setTab('builtin')}>
                      {t('transcriptions.templates.emptyMineJump')}
                    </button>
                    {t('transcriptions.templates.emptyMineSuffix')}
                  </>
                ) : (
                  t('transcriptions.templates.emptyMineNoBuiltin')
                )}
              </p>
            )}
          </ViewTabPanel>

          {showTeamTab ? (
            <ViewTabPanel id="team" active={tab === 'team'} className="tp-panel">
              {/* The permission holds for the whole list, so it is said once here
                  rather than tagged onto every row. */}
              <p className="tp-scopeline">
                {teamName
                  ? t('transcriptions.templates.teamLead', { workspace: teamName })
                  : t('transcriptions.templates.teamLeadNoName')}
              </p>
              {team.length > 0 ? (
                team.map((template) => (
                  <TemplateRow
                    key={template.id}
                    template={template}
                    t={t}
                    onEdit={() => openExisting(template)}
                    onDuplicate={() => openCopyDraft(template)}
                    onDelete={() => void handleDelete(template)}
                  />
                ))
              ) : (
                <p className="tp-empty">
                  {t('transcriptions.templates.emptyTeam')}
                  {canWriteTeam ? (
                    <button className="tp-jump" type="button" onClick={openNew}>
                      {t('transcriptions.templates.emptyTeamCta')}
                    </button>
                  ) : null}
                </p>
              )}
            </ViewTabPanel>
          ) : null}

          {/* Read-only by the SHAPE of the row actions (看內容 / 複製成我的),
              with no sentence explaining it — the actions already said so. */}
          <ViewTabPanel id="builtin" active={tab === 'builtin'} className="tp-panel">
            {builtin.length > 0 ? (
              builtin.map((template) => (
                <TemplateRow
                  key={template.id}
                  template={template}
                  t={t}
                  onEdit={() => openExisting(template)}
                  onDuplicate={() => openCopyDraft(template)}
                />
              ))
            ) : (
              <p className="tp-empty">{t('transcriptions.templates.emptyBuiltin')}</p>
            )}
          </ViewTabPanel>
        </>
      ) : null}

      {draft ? (
        <TemplateDrawer
          draft={draft}
          onChange={setDraft}
          onClose={() => setDraft(null)}
          onSave={() => void handleSave()}
          onDelete={() => {
            const target = templates.find((template) => template.id === draft.id)
            if (target) void handleDelete(target)
          }}
          saving={saving}
          writableScopes={writableScopes}
          teamName={teamName}
          t={t}
        />
      ) : null}
    </>
  )
}

function TemplateRow({
  template,
  t,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  template: NotesTemplate
  t: Translate
  onEdit: () => void
  onDuplicate: () => void
  /** Absent for built-ins — nobody deletes those from here. */
  onDelete?: () => void
}) {
  return (
    <div className="tp-row">
      <span className="tp-main">
        <span className="tp-n">
          {template.name}
          {template.isDefault ? <Tag>{t('transcriptions.templates.defaultTag')}</Tag> : null}
        </span>
        <span className="tp-d">
          {template.description || t('transcriptions.templates.noDescription')}
        </span>
      </span>
      <span className="tp-act">
        <Button variant="secondary" size="sm" onClick={onEdit}>
          {template.isBuiltin
            ? t('transcriptions.templates.view')
            : t('transcriptions.templates.edit')}
        </Button>
        <Button variant="secondary" size="sm" onClick={onDuplicate}>
          {template.isBuiltin
            ? t('transcriptions.templates.copyToMine')
            : t('transcriptions.templates.duplicate')}
        </Button>
        {/* Delete lived only inside the edit drawer, which is not where anyone
            looks for it — the row is where you decide a template is no longer
            wanted. */}
        {onDelete ? (
          <Button variant="secondary" size="sm" onClick={onDelete}>
            {t('transcriptions.templates.delete')}
          </Button>
        ) : null}
      </span>
    </div>
  )
}

function TemplateDrawer({
  draft,
  onChange,
  onClose,
  onSave,
  onDelete,
  saving,
  writableScopes,
  teamName,
  t,
}: {
  draft: Draft
  onChange: (draft: Draft) => void
  onClose: () => void
  onSave: () => void
  onDelete: () => void
  saving: boolean
  writableScopes: Scope[]
  /** The team workspace's name; null falls back to「團隊成員」wording. */
  teamName: string | null
  t: Translate
}) {
  const scopeIdBase = useId()
  const editingTeamTemplate = !draft.readOnly && draft.id !== null && draft.scope === 'organization'
  const title = draft.readOnly
    ? t('transcriptions.templates.drawerBuiltinTitle', { name: draft.name })
    : draft.id === null
      ? t('transcriptions.templates.drawerNewTitle')
      : editingTeamTemplate
        ? t('transcriptions.templates.drawerEditTeamTitle')
        : t('transcriptions.templates.drawerEditTitle')
  const subtitle = draft.readOnly
    ? t('transcriptions.templates.drawerBuiltinSub')
    : !editingTeamTemplate
      ? t('transcriptions.templates.drawerEditSub')
      : teamName
        ? t('transcriptions.templates.drawerEditTeamSub', { workspace: teamName })
        : t('transcriptions.templates.drawerEditTeamSubNoName')
  // What 設為預設 does depends on who the template is for: a team default
  // pre-selects for everyone who has not set their own.
  const setDefaultNote =
    draft.scope !== 'organization'
      ? t('transcriptions.templates.setDefaultNote')
      : teamName
        ? t('transcriptions.templates.setDefaultNoteOrg', { workspace: teamName })
        : t('transcriptions.templates.setDefaultNoteOrgNoName')
  const scopeNote = (scope: Scope) =>
    scope === 'personal'
      ? t('transcriptions.templates.scopePersonalNote')
      : teamName
        ? t('transcriptions.templates.scopeOrganizationNote', { workspace: teamName })
        : t('transcriptions.templates.scopeOrganizationNoteNoName')
  // Also gates 複製成我的 on a built-in: the API refuses to duplicate a source
  // whose tables will not render (422 source_template_unrenderable), and the
  // preview above has already shown the author what is wrong with it.
  const savable = useMemo(
    () => canSaveTemplate(findTemplateProblems(draft.markdown)),
    [draft.markdown]
  )

  return (
    <KiaoModal
      open
      onClose={onClose}
      className="kiao-modal--drawer kiao-tpl-drawer"
      title={title}
      subtitle={subtitle}
      closeLabel={t('transcriptions.templates.close')}
      footer={
        <>
          {draft.id !== null && !draft.readOnly ? (
            <Button variant="secondary" size="sm" onClick={onDelete}>
              {t('transcriptions.templates.delete')}
            </Button>
          ) : null}
          <span className="spacer" />
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('transcriptions.templates.cancel')}
          </Button>
          <Button variant="primary" size="sm" disabled={saving || !savable} onClick={onSave}>
            {draft.readOnly
              ? t('transcriptions.templates.copyAsMine')
              : t('transcriptions.templates.save')}
          </Button>
        </>
      }
    >
      <Field label={t('transcriptions.templates.fieldName')} htmlFor="tpl-name">
        <Input
          id="tpl-name"
          value={draft.name}
          readOnly={draft.readOnly}
          placeholder={t('transcriptions.templates.fieldNamePlaceholder')}
          maxLength={120}
          onChange={(event) => onChange({ ...draft, name: event.target.value })}
        />
      </Field>

      <Field label={t('transcriptions.templates.fieldDesc')} htmlFor="tpl-desc">
        <Input
          id="tpl-desc"
          value={draft.description}
          readOnly={draft.readOnly}
          placeholder={t('transcriptions.templates.fieldDescPlaceholder')}
          maxLength={240}
          onChange={(event) => onChange({ ...draft, description: event.target.value })}
        />
      </Field>

      {/* Scope only appears when there is a choice to make: in a personal
          context the org option does not exist, and a single-option control is
          not a choice. It sits before the body because it decides who the
          template is for, and it cannot be changed after creation, so both
          consequences are spelled out side by side rather than behind a toggle. */}
      {!draft.readOnly && writableScopes.length > 1 && draft.id === null ? (
        <Field label={t('transcriptions.templates.fieldScope')}>
          <div
            className="tp-scopeopts"
            role="radiogroup"
            aria-label={t('transcriptions.templates.fieldScope')}
          >
            {writableScopes.map((scope) => (
              <label key={scope} className="tp-scopeopt">
                <input
                  type="radio"
                  name={`${scopeIdBase}-scope`}
                  value={scope}
                  checked={draft.scope === scope}
                  onChange={() => onChange({ ...draft, scope })}
                  aria-labelledby={`${scopeIdBase}-${scope}-title`}
                  aria-describedby={`${scopeIdBase}-${scope}-note`}
                />
                <b id={`${scopeIdBase}-${scope}-title`}>
                  {scope === 'personal'
                    ? t('transcriptions.templates.scopePersonal')
                    : t('transcriptions.templates.scopeOrganization')}
                </b>
                <span id={`${scopeIdBase}-${scope}-note`}>{scopeNote(scope)}</span>
              </label>
            ))}
          </div>
          <span className="note">{t('transcriptions.templates.scopeLocked')}</span>
        </Field>
      ) : null}

      <div className="tpl-split">
        <Field label={t('transcriptions.templates.fieldMarkdown')} htmlFor="tpl-md">
          <Textarea
            id="tpl-md"
            className="tp-md"
            value={draft.markdown}
            readOnly={draft.readOnly}
            spellCheck={false}
            onChange={(event) => onChange({ ...draft, markdown: event.target.value })}
          />
          <span className="note">{t('transcriptions.templates.fieldMarkdownNote')}</span>
        </Field>

        <TemplatePreview markdown={draft.markdown} />
      </div>

      {!draft.readOnly ? (
        <label className="tp-def">
          <span className="t">
            <b>{t('transcriptions.templates.setDefault')}</b>
            <span>{setDefaultNote}</span>
          </span>
          <Switch
            checked={draft.isDefault}
            aria-label={t('transcriptions.templates.setDefault')}
            onChange={(next) => onChange({ ...draft, isDefault: next })}
          />
        </label>
      ) : (
        <div className="tp-lock">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            aria-hidden="true"
          >
            <rect x="5" y="11" width="14" height="9" rx="2" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" />
          </svg>
          <span>{t('transcriptions.templates.builtinLock')}</span>
        </div>
      )}
    </KiaoModal>
  )
}
